import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {CnSimulationApiService} from '../../../service/api/cn-simulation-api/cn-simulation-api.service';
import {CommissionExpectationService} from '../../../service/api/commission-expectation/commission-expectation.service';
import {CommissionModelService} from '../../../service/api/commission-model/commission-model.service';
import {CommissionExpectationDto} from '../../../model/dto/entity/commission-expectation/commission-expectation.dto';
import {CommissionModelDto} from '../../../model/dto/entity/commission-model/commission-model.dto';
import {CnSimulateRequestDto} from '../../../model/dto/api/cn-simulate-request.dto';
import {CnRunStatusDto} from '../../../model/dto/api/cn-run-status.dto';
import {ObjectRequestList} from 'features/core/shared/dto/object/request/object-request-list';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';

/**
* Rule preview / Smart-Guide (FR-17).
*
* Lets the configurator compute a rule against an example contract and see the resulting
* 'Herleitung' (FR-14 format) BEFORE saving — so the effect of the configuration is visible.
*
* Flow:
*  1. The user enters / selects an example contract id.
*  2. simulate() runs for the owning model scoped to that single contract (runId polling).
*  3. On SUCCESS, the matching CommissionExpectation (rule × contract) is loaded.
*  4. Its calculationTrace is rendered via cn-expectation-trace.
*
* NFR-CleanCore: NO calculation in the FE — the backend computes the trace; this component
* only triggers the run and displays the result. Loading / empty / error states per FR-16.
*/
@Component({
    selector: 'mvs-cn-rule-preview',
    templateUrl: './cn-rule-preview.component.html',
    styleUrls: ['./cn-rule-preview.component.scss'],
    standalone: false
})
export class CnRulePreviewComponent implements OnInit, OnChanges, OnDestroy {

    /** Owning rule id (FK commissionRuleDtoId). */
    @Input() commissionRuleId: number;

    /** Owning model id — required as the simulate endpoint is keyed by model. */
    @Input() commissionModelId: number;

    /** Example contract id entered / selected by the configurator. */
    exampleContractId: number | null = null;

    /** UI state. */
    busy: boolean = false;
    progress: number = 0;
    errorMessage: string | null = null;
    expectation: CommissionExpectationDto | null = null;
    notFound: boolean = false;
    initialized: boolean = false;

    constructor(
        private simulationApiService: CnSimulationApiService,
        private expectationService: CommissionExpectationService,
        private commissionModelService: CommissionModelService
    ) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /**
    * Initialize the preview panel. No data is fetched until the configurator runs a preview.
    */
    initComponent(): void {
        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['commissionRuleId'] || changes['commissionModelId']) {
            // Reset any previous result when the bound rule / model changes.
            this.expectation = null;
            this.notFound = false;
            this.errorMessage = null;
        }
    }

    ngOnDestroy(): void {
    }

    /**
    * Run the preview for the selected example contract.
    *
    * TM-1000001915 (B8): The simulate endpoint requires a mandatory partnerId in contractFilter
    * (CnContractFilterDto.partnerId; productIds filters the cm_product FK, NOT the contract).
    * We resolve the partner from the owning model (model.partner -> partnerDtoId) first, then
    * scope the run to that partner. The contract dimension is not a supported filter field, so the
    * example contract is matched through its partner stock and surfaced via loadExpectationTrace().
    */
    runPreview(): void {
        if (!this.exampleContractId || !this.commissionModelId) {
            return;
        }

        this.busy = true;
        this.progress = 0;
        this.errorMessage = null;
        this.expectation = null;
        this.notFound = false;

        // Resolve the mandatory partnerId from the owning model before simulating (B8).
        this.commissionModelService.get(this.commissionModelId).subscribe({
            next: (model: CommissionModelDto) => {
                const partnerId = (model as unknown as { partnerDtoId?: number }).partnerDtoId;
                if (!partnerId) {
                    this.busy = false;
                    this.errorMessage =
                        'Dem Provisionsmodell ist keine Gesellschaft zugeordnet — ' +
                        'die Vorschau benötigt eine Gesellschaft.';
                    return;
                }
                this.simulateForPartner(partnerId);
            },
            error: () => {
                this.busy = false;
                this.errorMessage = 'Das Provisionsmodell konnte für die Vorschau nicht geladen werden.';
            }
        });
    }

    /**
    * Trigger the simulate run scoped to the resolved partner and poll until done.
    */
    private simulateForPartner(partnerId: number): void {
        const request: CnSimulateRequestDto = {
            contractFilter: {partnerId: partnerId},
            horizonYears: 1,
            persist: true
        };

        this.simulationApiService.simulateAndPoll(this.commissionModelId, request).subscribe({
            next: (value) => {
                if (this.isRunStatus(value)) {
                    this.progress = value.progress ?? 0;
                } else {
                    // Simulation completed — load the matching expectation trace.
                    this.loadExpectationTrace();
                }
            },
            error: (err) => {
                this.busy = false;
                this.errorMessage = err?.message || 'Die Vorschau konnte nicht berechnet werden.';
            }
        });
    }

    /**
    * Load the CommissionExpectation for (rule × example contract) and expose its trace.
    *
    * TM-1000001915 (B9): this is a DB re-read (expectationService.list), so the backend's
    * @PostLoad mirror hydrates expectedGrossAmount — Brutto then surfaces in the trace summary
    * alongside the unchanged Netto. This narrows the partner-scoped run to the exact example
    * contract via the e.contract.id filter (the simulate contractFilter has no contract dimension).
    *
    * TM-1000001915 (B14): this expectation fetch goes through MvsCrudService.list() ->
    * FilterService.toPredicate(), which calls root.get(field) directly: it supports neither the
    * 'e.' alias prefix nor dotted paths. CommissionExpectation exposes commissionRule/contract as
    * @ManyToOne FK relations, so the filter MUST use the PLAIN attribute names 'commissionRule' /
    * 'contract' (FilterService auto-appends .get("id") for entity-typed fields). The earlier flat
    * 'e.commissionRuleId' and the dotted 'e.commissionRule.id' both failed to resolve and returned
    * an empty trace. (Widget/QL data providers use 'e.'-prefixed names — a different endpoint.)
    */
    private loadExpectationTrace(): void {
        const filters: FilterCriteria[] = [
            FilterCriteria.create('commissionRule', FilterCriteria.cOperatorEqual, this.commissionRuleId),
            FilterCriteria.create('contract', FilterCriteria.cOperatorEqual, this.exampleContractId)
        ];
        const request = ObjectRequestList.createBasic(true, filters, [new Sorting('id', false)]);

        this.expectationService.list(request).subscribe({
            next: (res) => {
                this.busy = false;
                const entry = res.entries?.[0] as CommissionExpectationDto;
                if (entry) {
                    this.expectation = entry;
                } else {
                    this.notFound = true;
                }
            },
            error: () => {
                this.busy = false;
                this.errorMessage = 'Die Herleitung des Beispielvertrags konnte nicht geladen werden.';
            }
        });
    }

    private isRunStatus(value: any): value is CnRunStatusDto {
        return value && typeof value.status === 'string' && 'progress' in value;
    }
}
